import React, { useState } from 'react';
import { Button, Select } from '@uclaea/react-component-library';
import '../css/PrintSettings.css';

export interface PrintConfig {
  labelSize: string;
  templateId: number;
  widthDots: number;
  lengthDots: number;
}

interface LabelSizeOption {
  value: string;
  label: string;
  id: string;
  widthDots: number;
  lengthDots: number;
}

const LABEL_SIZES: LabelSizeOption[] = [
  { value: '57x89',  id: '57x89',  label: '2.25" x 3.5" (57 x 89 mm)',  widthDots: 456, lengthDots: 710 },
  { value: '57x102', id: '57x102', label: '2.25" x 4" (57 x 102 mm)',    widthDots: 456, lengthDots: 812 },
  { value: '76x102', id: '76x102', label: '3" x 4" (76 x 102 mm)',       widthDots: 609, lengthDots: 812 },
  { value: '102x76', id: '102x76', label: '4" x 3" (102 x 76 mm)',       widthDots: 812, lengthDots: 609 },
];

export const DEFAULT_PRINT_CONFIG: PrintConfig = {
  labelSize: '57x89',
  templateId: 1,
  widthDots: 456,
  lengthDots: 710,
};

const TEMPLATES = [
  { id: 1, name: 'Template 1', description: 'Logo left, name center, notes bottom' },
  { id: 2, name: 'Template 2', description: 'Large name, no notes' },
  { id: 3, name: 'Template 3', description: 'Name + notes side by side' },
];

interface PrintSettingsProps {
  settings: PrintConfig;
  onApply: (settings: PrintConfig) => void;
}

const PrintSettings: React.FC<PrintSettingsProps> = ({ settings, onApply }) => {
  const [expanded, setExpanded] = useState(false);
  const [localSize, setLocalSize] = useState(settings.labelSize);
  const [localTemplate, setLocalTemplate] = useState(settings.templateId);

  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalSize(e.target.value);
  };

  const handleApply = () => {
    const size = LABEL_SIZES.find(s => s.value === localSize) ?? LABEL_SIZES[0];
    onApply({
      labelSize: size.value,
      templateId: localTemplate,
      widthDots: size.widthDots,
      lengthDots: size.lengthDots,
    });
  };

  return (
    <>
      <span
        className="print-settings-toggle"
        role="link"
        tabIndex={0}
        onClick={() => setExpanded(prev => !prev)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setExpanded(prev => !prev); }}
        aria-expanded={expanded}
      >
        {expanded ? 'Hide Settings' : 'Print Settings'}
        <span className={`print-settings-chevron${expanded ? ' print-settings-chevron--open' : ''}`}>&#9662;</span>
      </span>

      {expanded && (
        <div className="print-settings-body">
          <div className="print-settings-field">
            <div className="print-settings-select-wrapper">
              <Select
                label="Label Size"
                value={localSize}
                onChange={handleSizeChange}
                options={LABEL_SIZES}
              />
            </div>
          </div>

          <div className="print-settings-field">
            <span className="print-settings-label">Nametag Template</span>
            <div className="print-settings-templates">
              {TEMPLATES.map(t => (
                <label
                  key={t.id}
                  className={`print-settings-template-card${localTemplate === t.id ? ' print-settings-template-card--selected' : ''}`}
                >
                  <div className="print-settings-template-preview">
                    <span className="print-settings-template-preview-text">{t.name}</span>
                  </div>
                  <div className="print-settings-template-info">
                    <input
                      type="radio"
                      name="template"
                      value={t.id}
                      checked={localTemplate === t.id}
                      onChange={() => setLocalTemplate(t.id)}
                    />
                    <span className="print-settings-template-description">{t.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="print-settings-actions">
            <Button variant="primary" label="Apply" onClick={handleApply} />
          </div>
        </div>
      )}
    </>
  );
};

export default PrintSettings;
